<script setup>
import { onMounted, computed, ref, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { debounce } from '@chatwoot/utils';
import { useUISettings } from 'dashboard/composables/useUISettings';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import ClientsListLayout from 'dashboard/components-next/Clients/ClientsListLayout.vue';
import ClientsList from 'dashboard/components-next/Clients/Pages/ClientsList.vue';
import ClientEmptyState from 'dashboard/components-next/Clients/EmptyState/ClientEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const DEFAULT_SORT_FIELD = 'last_activity_at';
const DEBOUNCE_DELAY = 300;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const clients = useMapGetter('clients/getClientsList');
// const uiFlags = useMapGetter('clients/getUIFlags');
// const customViewsUiFlags = useMapGetter('customViews/getUIFlags');
const segments = useMapGetter('customViews/getClientCustomViews');
const appliedFilters = useMapGetter('clients/getAppliedClientFilters');
const meta = useMapGetter('clients/getMeta');

const searchQuery = computed(() => route.query?.search);
const searchValue = ref(searchQuery.value || '');
const pageNumber = computed(() => Number(route.query?.page) || 1);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { clients_sort_by: clientSortBy = '' } = uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(clientSortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeLabel = computed(() => route.params.label);
const activeSegmentId = computed(() => route.params.segmentId);
const isFetchingList = computed(
  // () => uiFlags.value.isFetching || customViewsUiFlags.value.isFetching
  () => false
);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);
const activeSegment = computed(() => {
  if (!activeSegmentId.value) return undefined;
  return segments.value.find(view => view.id === Number(activeSegmentId.value));
});

const hasClients = computed(() => {
  return clients?.value?.length > 0;
});
const isClientIndexView = computed(
  () => route.name === 'clients_dashboard_index' && pageNumber.value === 1
);
const isActiveView = computed(() => route.name === 'clients_dashboard_active');
const hasAppliedFilters = computed(() => {
  return (appliedFilters.value?.length ?? 0) > 0;
});

const showEmptyStateLayout = computed(() => {
  return (
    !hasClients.value &&
    !searchQuery.value &&
    isClientIndexView.value &&
    !hasAppliedFilters.value
  );
});
const showEmptyText = computed(() => {
  return (
    (searchQuery.value ||
      hasAppliedFilters.value ||
      !isClientIndexView.value) &&
    !hasClients.value
  );
});

const headerTitle = computed(() => {
  if (searchQuery.value) return t('CLIENTS_LAYOUT.HEADER.SEARCH_TITLE');
  if (isActiveView.value) return t('CLIENTS_LAYOUT.HEADER.ACTIVE_TITLE');
  if (activeSegmentId.value) return activeSegment.value?.name;
  if (activeLabel.value) return `#${activeLabel.value}`;
  return t('CLIENTS_LAYOUT.HEADER.TITLE');
});

const emptyStateMessage = computed(() => {
  if (isActiveView.value)
    return t('CLIENTS_LAYOUT.EMPTY_STATE.ACTIVE_EMPTY_STATE_TITLE');
  if (!searchQuery.value || hasAppliedFilters.value)
    return t('CLIENTS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE');
  return t('CLIENTS_LAYOUT.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE');
});

const updatePageParam = (page, search = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
    ...(search ? { search } : {}),
  };

  if (!search) {
    delete query.search;
  }

  router.replace({ query });
};

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const getCommonFetchParams = (page = 1) => ({
  page,
  sortAttr: buildSortAttr(),
  label: activeLabel.value,
});

const fetchClients = async (page = 1) => {
  await store.dispatch('clients/clearClientFilters');
  await store.dispatch('clients/get', getCommonFetchParams(page));
  updatePageParam(page);
};

const fetchSavedOrAppliedFilteredClient = async (payload, page = 1) => {
  if (!activeSegmentId.value && !hasAppliedFilters.value) return;
  await store.dispatch('clients/filter', {
    ...getCommonFetchParams(page),
    queryPayload: payload,
  });
  updatePageParam(page);
};

const fetchActiveClients = async (page = 1) => {
  await store.dispatch('clients/clearClientFilters');
  await store.dispatch('clients/active', {
    page,
    sortAttr: buildSortAttr(),
  });
  updatePageParam(page);
};

const searchClients = debounce(async (value, page = 1) => {
  await store.dispatch('clients/clearClientFilters');
  searchValue.value = value;

  if (!value) {
    updatePageParam(page);
    await fetchClients(page);
    return;
  }

  updatePageParam(page, value);
  await store.dispatch('clients/search', {
    ...getCommonFetchParams(page),
    search: encodeURIComponent(value),
  });
}, DEBOUNCE_DELAY);

const fetchClientsBasedOnContext = async page => {
  updatePageParam(page, searchValue.value);
  if (isFetchingList.value) return;
  if (searchQuery.value) {
    await searchClients(searchQuery.value, page);
    return;
  }
  // Reset the search value when we change the view
  searchValue.value = '';
  // If we're on the active route, fetch active clients
  if (isActiveView.value) {
    await fetchActiveClients(page);
    return;
  }
  // If there are applied filters or active segment with query
  if (
    (hasAppliedFilters.value || activeSegment.value?.query) &&
    !activeLabel.value
  ) {
    const queryPayload =
      activeSegment.value?.query || filterQueryGenerator(appliedFilters.value);
    await fetchSavedOrAppliedFilteredClient(queryPayload, page);
    return;
  }
  // Default case: fetch regular clients + label
  await fetchClients(page);
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    clients_sort_by: buildSortAttr(),
  });

  if (searchQuery.value) {
    await searchClients(searchValue.value);
    return;
  }

  if (isActiveView.value) {
    await fetchActiveClients();
    return;
  }

  await (activeSegmentId.value || hasAppliedFilters.value
    ? fetchSavedOrAppliedFilteredClient(
        activeSegmentId.value
          ? activeSegment.value?.query
          : filterQueryGenerator(appliedFilters.value)
      )
    : fetchClients());
};

const createClient = async client => {
  await store.dispatch('clients/create', client);
};

watch(
  () => uiSettings.value?.clients_sort_by,
  newSortBy => {
    if (newSortBy) {
      const { sort, order } = parseSortSettings(newSortBy);
      sortState.activeSort = sort;
      sortState.activeOrdering = order;
    }
  },
  { immediate: true }
);

watch(
  [activeLabel, activeSegment, isActiveView],
  () => {
    fetchClientsBasedOnContext(pageNumber.value);
  },
  { deep: true }
);

watch(searchQuery, value => {
  if (isFetchingList.value) return;
  searchValue.value = value || '';
  // Reset the view if there is search query when we click on the sidebar group
  if (value === undefined) {
    if (
      isActiveView.value ||
      activeLabel.value ||
      activeSegment.value ||
      hasAppliedFilters.value
    )
      return;
    fetchClients();
  }
});

onMounted(async () => {
  if (!activeSegmentId.value) {
    if (searchQuery.value) {
      await searchClients(searchQuery.value, pageNumber.value);
      return;
    }
    if (isActiveView.value) {
      await fetchActiveClients(pageNumber.value);
      return;
    }
    await fetchClients(pageNumber.value);
  } else if (activeSegment.value && activeSegmentId.value) {
    await fetchSavedOrAppliedFilteredClient(
      activeSegment.value.query,
      pageNumber.value
    );
  }
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <ClientsListLayout
      :search-value="searchValue"
      :header-title="headerTitle"
      :current-page="currentPage"
      :total-items="totalItems"
      :show-pagination-footer="!isFetchingList && hasClients"
      :active-sort="sortState.activeSort"
      :active-ordering="sortState.activeOrdering"
      :active-segment="activeSegment"
      :segments-id="activeSegmentId"
      :is-fetching-list="isFetchingList"
      :has-applied-filters="hasAppliedFilters"
      @update:current-page="fetchClientsBasedOnContext"
      @search="searchClients"
      @update:sort="handleSort"
      @apply-filter="fetchSavedOrAppliedFilteredClient"
      @clear-filters="fetchClients"
    >
      <div
        v-if="isFetchingList"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <ClientEmptyState
          v-if="showEmptyStateLayout"
          class="pt-14"
          :title="t('CLIENTS_LAYOUT.EMPTY_STATE.TITLE')"
          :subtitle="t('CLIENTS_LAYOUT.EMPTY_STATE.SUBTITLE')"
          :button-label="t('CLIENTS_LAYOUT.EMPTY_STATE.BUTTON_LABEL')"
          @create="createClient"
        />

        <div
          v-else-if="showEmptyText"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ emptyStateMessage }}
          </span>
        </div>

        <ClientsList v-else :clients="clients" />
      </template>
    </ClientsListLayout>
  </div>
</template>
